import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getBestPractices,
  getBestPractice,
  createBestPractice,
  updateBestPractice,
  deleteBestPractice,
  rechunkBestPractice,
  scanGitHubRepo,
  importFromGitHub,
  previewWebUrl,
  importFromWeb,
  BestPracticeListItem,
  BestPracticeCreate,
  BestPracticeUpdate,
  BestPractice,
  UseCase,
  GitHubImportRequest,
  GitHubImportResponse,
  GitHubScanResponse,
  WebImportRequest,
  WebPreviewResponse,
  WebImportResponse,
} from '../services/api'

const USE_CASE_OPTIONS: { value: UseCase | ''; label: string }[] = [
  { value: '', label: 'All Use Cases' },
  { value: 'aidw', label: 'AIDW' },
  { value: 'caf', label: 'CAF' },
  { value: 'waf', label: 'WAF' },
  { value: 'other', label: 'Other' },
]

const SOURCE_TYPE_OPTIONS = [
  { value: 'curated', label: 'Curated' },
  { value: 'web', label: 'Web' },
  { value: 'upload', label: 'Upload' },
]

const useCaseBadgeColors: Record<string, { bg: string; text: string; border: string }> = {
  aidw: { bg: 'rgba(251, 191, 36, 0.2)', text: '#fbbf24', border: 'rgba(251, 191, 36, 0.3)' },
  caf: { bg: 'rgba(59, 130, 246, 0.2)', text: '#60a5fa', border: 'rgba(59, 130, 246, 0.3)' },
  waf: { bg: 'rgba(34, 197, 94, 0.2)', text: '#22c55e', border: 'rgba(34, 197, 94, 0.3)' },
  other: { bg: 'rgba(148, 163, 184, 0.2)', text: '#94a3b8', border: 'rgba(148, 163, 184, 0.3)' },
}

export default function BestPractices() {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [selectedBp, setSelectedBp] = useState<BestPractice | null>(null)
  const [useCaseFilter, setUseCaseFilter] = useState<string>('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [search, setSearch] = useState('')

  const [formData, setFormData] = useState<BestPracticeCreate>({
    use_case: 'caf',
    title: '',
    description: '',
    content: '',
    source_type: 'curated',
    source_url: '',
  })

  const [editFormData, setEditFormData] = useState<BestPracticeUpdate>({})

  // GitHub import state
  const [importData, setImportData] = useState<GitHubImportRequest>({
    owner: 'MicrosoftDocs',
    repo: 'cloud-adoption-framework',
    use_case: 'caf',
    path: 'docs',
    branch: 'main',
  })
  const [scanResult, setScanResult] = useState<GitHubScanResponse | null>(null)
  const [importResult, setImportResult] = useState<GitHubImportResponse | null>(null)

  // Web import state
  const [showWebImportModal, setShowWebImportModal] = useState(false)
  const [webImportData, setWebImportData] = useState<WebImportRequest>({
    url: '',
    use_case: 'caf',
  })
  const [webPreviewResult, setWebPreviewResult] = useState<WebPreviewResponse | null>(null)
  const [webImportResult, setWebImportResult] = useState<WebImportResponse | null>(null)

  const { data: bestPractices = [], isLoading } = useQuery({
    queryKey: ['best-practices', useCaseFilter, includeInactive],
    queryFn: async () => {
      const response = await getBestPractices({
        use_case: useCaseFilter || undefined,
        include_inactive: includeInactive,
      })
      return response.data
    },
  })

  const createMutation = useMutation({
    mutationFn: (data: BestPracticeCreate) => createBestPractice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['best-practices'] })
      setShowCreateModal(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: BestPracticeUpdate }) =>
      updateBestPractice(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['best-practices'] })
      setShowEditModal(false)
      setSelectedBp(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteBestPractice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['best-practices'] })
    },
  })

  const rechunkMutation = useMutation({
    mutationFn: (id: string) => rechunkBestPractice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['best-practices'] })
    },
  })

  const scanMutation = useMutation({
    mutationFn: (data: GitHubImportRequest) => scanGitHubRepo(data),
    onSuccess: (response) => {
      setScanResult(response.data)
    },
  })

  const [importError, setImportError] = useState<string | null>(null)

  const importMutation = useMutation({
    mutationFn: (data: GitHubImportRequest) => importFromGitHub(data),
    onSuccess: (response) => {
      setImportResult(response.data)
      setImportError(null)
      queryClient.invalidateQueries({ queryKey: ['best-practices'] })
    },
    onError: (error: Error) => {
      console.error('Import failed:', error)
      setImportError(error.message || 'Import failed. The request may have timed out.')
    },
  })

  const webPreviewMutation = useMutation({
    mutationFn: (data: WebImportRequest) => previewWebUrl(data),
    onSuccess: (response) => {
      setWebPreviewResult(response.data)
    },
  })

  const webImportMutation = useMutation({
    mutationFn: (data: WebImportRequest) => importFromWeb(data),
    onSuccess: (response) => {
      setWebImportResult(response.data)
      if (response.data.success) {
        queryClient.invalidateQueries({ queryKey: ['best-practices'] })
      }
    },
  })

  const resetForm = () => {
    setFormData({
      use_case: 'caf',
      title: '',
      description: '',
      content: '',
      source_type: 'curated',
      source_url: '',
    })
  }

  const resetImport = () => {
    setImportData({
      owner: 'MicrosoftDocs',
      repo: 'cloud-adoption-framework',
      use_case: 'caf',
      path: 'docs',
      branch: 'main',
    })
    setScanResult(null)
    setImportResult(null)
    setImportError(null)
  }

  const resetWebImport = () => {
    setWebImportData({
      url: '',
      use_case: 'caf',
    })
    setWebPreviewResult(null)
    setWebImportResult(null)
  }

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate(formData)
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedBp) {
      updateMutation.mutate({ id: selectedBp.id, data: editFormData })
    }
  }

  const openEditModal = async (bp: BestPracticeListItem) => {
    try {
      const response = await getBestPractice(bp.id)
      const fullBp = response.data
      setSelectedBp(fullBp)
      setEditFormData({
        title: fullBp.title,
        description: fullBp.description,
        content: fullBp.content,
        use_case: fullBp.use_case,
        source_url: fullBp.source_url,
        is_active: fullBp.is_active,
      })
      setShowEditModal(true)
    } catch (error) {
      console.error('Failed to load best practice:', error)
    }
  }

  const filteredBestPractices = bestPractices.filter((bp) => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      bp.title.toLowerCase().includes(searchLower) ||
      (bp.description?.toLowerCase().includes(searchLower) ?? false)
    )
  })

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.6)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(100, 180, 255, 0.1)',
    borderRadius: '16px',
  }

  const inputStyle = {
    background: 'rgba(30, 41, 59, 0.5)',
    border: '1px solid rgba(100, 180, 255, 0.15)',
    color: '#e2e8f0',
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: '#f1f5f9' }}>
            Best Practices
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
            Curated knowledge base for RAG-enhanced document generation
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowWebImportModal(true)}
            className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#e2e8f0',
              border: '1px solid rgba(100, 180, 255, 0.2)',
            }}
          >
            <i className="fa-solid fa-globe"></i>
            <span>Import from URL</span>
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 hover:scale-105"
            style={{
              background: 'rgba(30, 41, 59, 0.8)',
              color: '#e2e8f0',
              border: '1px solid rgba(100, 180, 255, 0.2)',
            }}
          >
            <i className="fa-brands fa-github"></i>
            <span>Import from GitHub</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
              color: 'white',
              boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
            }}
          >
            <i className="fa-solid fa-plus"></i>
            <span>Add Best Practice</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 rounded-xl" style={cardStyle}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search best practices..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={inputStyle}
            />
          </div>
          <select
            value={useCaseFilter}
            onChange={(e) => setUseCaseFilter(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm focus:outline-none"
            style={inputStyle}
          >
            {USE_CASE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <label
            className="flex items-center space-x-2 text-sm"
            style={{ color: '#94a3b8' }}
          >
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="rounded"
            />
            <span>Show inactive</span>
          </label>
        </div>
      </div>

      {/* Best Practices Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : filteredBestPractices.length === 0 ? (
        <div className="text-center py-16 rounded-xl" style={cardStyle}>
          <i
            className="fa-solid fa-book-open text-4xl mb-4"
            style={{ color: 'rgba(148, 163, 184, 0.5)' }}
          ></i>
          <p style={{ color: '#94a3b8' }}>No best practices found</p>
          <p
            className="text-sm mt-1"
            style={{ color: 'rgba(148, 163, 184, 0.6)' }}
          >
            Add curated content to enhance document generation
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBestPractices.map((bp) => (
            <BestPracticeCard
              key={bp.id}
              bestPractice={bp}
              onEdit={() => openEditModal(bp)}
              onDelete={() => {
                if (confirm(`Delete "${bp.title}"?`)) {
                  deleteMutation.mutate(bp.id)
                }
              }}
              onRechunk={() => {
                if (confirm(`Re-chunk "${bp.title}"? This will regenerate all embeddings.`)) {
                  rechunkMutation.mutate(bp.id)
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6"
            style={cardStyle}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: '#f1f5f9' }}>
                Add Best Practice
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  resetForm()
                }}
                style={{ color: '#94a3b8' }}
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: '#94a3b8' }}
                  >
                    Title
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Azure Landing Zone Best Practices"
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: '#94a3b8' }}
                  >
                    Use Case
                  </label>
                  <select
                    value={formData.use_case}
                    onChange={(e) => setFormData({ ...formData, use_case: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                    style={inputStyle}
                  >
                    {USE_CASE_OPTIONS.slice(1).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#94a3b8' }}
                >
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of this best practice..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: '#94a3b8' }}
                  >
                    Source Type
                  </label>
                  <select
                    value={formData.source_type}
                    onChange={(e) => setFormData({ ...formData, source_type: e.target.value as 'curated' | 'web' | 'upload' })}
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                    style={inputStyle}
                  >
                    {SOURCE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: '#94a3b8' }}
                  >
                    Source URL (optional)
                  </label>
                  <input
                    type="url"
                    value={formData.source_url || ''}
                    onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#94a3b8' }}
                >
                  Content
                </label>
                <textarea
                  required
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Paste or write the best practice content here. This will be chunked and embedded for RAG retrieval..."
                  rows={12}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false)
                    resetForm()
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ color: '#94a3b8', background: 'rgba(100, 116, 139, 0.2)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
                    color: 'white',
                  }}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Best Practice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedBp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6"
            style={cardStyle}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: '#f1f5f9' }}>
                Edit Best Practice
              </h2>
              <button
                onClick={() => {
                  setShowEditModal(false)
                  setSelectedBp(null)
                }}
                style={{ color: '#94a3b8' }}
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: '#94a3b8' }}
                  >
                    Title
                  </label>
                  <input
                    type="text"
                    required
                    value={editFormData.title || ''}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, title: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label
                    className="block text-sm font-medium mb-1"
                    style={{ color: '#94a3b8' }}
                  >
                    Use Case
                  </label>
                  <select
                    value={editFormData.use_case || ''}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, use_case: e.target.value })
                    }
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                    style={inputStyle}
                  >
                    {USE_CASE_OPTIONS.slice(1).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#94a3b8' }}
                >
                  Description
                </label>
                <textarea
                  value={editFormData.description || ''}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, description: e.target.value })
                  }
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#94a3b8' }}
                >
                  Source URL
                </label>
                <input
                  type="url"
                  value={editFormData.source_url || ''}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, source_url: e.target.value })
                  }
                  placeholder="https://..."
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#94a3b8' }}
                >
                  Content
                </label>
                <textarea
                  required
                  value={editFormData.content || ''}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, content: e.target.value })
                  }
                  rows={12}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
                <p
                  className="text-xs mt-1"
                  style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                >
                  Content changes will trigger automatic re-chunking and embedding
                </p>
              </div>

              <div>
                <label className="flex items-center space-x-2 text-sm" style={{ color: '#94a3b8' }}>
                  <input
                    type="checkbox"
                    checked={editFormData.is_active ?? true}
                    onChange={(e) =>
                      setEditFormData({ ...editFormData, is_active: e.target.checked })
                    }
                    className="rounded"
                  />
                  <span>Active (included in RAG searches)</span>
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false)
                    setSelectedBp(null)
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ color: '#94a3b8', background: 'rgba(100, 116, 139, 0.2)' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
                    color: 'white',
                  }}
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GitHub Import Modal */}
      {showImportModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6"
            style={cardStyle}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: '#f1f5f9' }}>
                <i className="fa-brands fa-github mr-2"></i>
                Import from GitHub
              </h2>
              <button
                onClick={() => {
                  setShowImportModal(false)
                  resetImport()
                }}
                style={{ color: '#94a3b8' }}
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              {/* Repository Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Owner
                  </label>
                  <input
                    type="text"
                    value={importData.owner}
                    onChange={(e) => setImportData({ ...importData, owner: e.target.value })}
                    placeholder="MicrosoftDocs"
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Repository
                  </label>
                  <input
                    type="text"
                    value={importData.repo}
                    onChange={(e) => setImportData({ ...importData, repo: e.target.value })}
                    placeholder="cloud-adoption-framework"
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Path (optional)
                  </label>
                  <input
                    type="text"
                    value={importData.path || ''}
                    onChange={(e) => setImportData({ ...importData, path: e.target.value })}
                    placeholder="docs"
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Branch
                  </label>
                  <input
                    type="text"
                    value={importData.branch || 'main'}
                    onChange={(e) => setImportData({ ...importData, branch: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Use Case
                  </label>
                  <select
                    value={importData.use_case}
                    onChange={(e) => setImportData({ ...importData, use_case: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                    style={inputStyle}
                  >
                    {USE_CASE_OPTIONS.slice(1).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Max Files (optional)
                  </label>
                  <input
                    type="number"
                    value={importData.max_files || ''}
                    onChange={(e) => setImportData({ ...importData, max_files: e.target.value ? parseInt(e.target.value) : undefined })}
                    placeholder="All files"
                    min={1}
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                  <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                    Limit imports to avoid timeouts
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    GitHub Token (optional)
                  </label>
                  <input
                    type="password"
                    value={importData.github_token || ''}
                    onChange={(e) => setImportData({ ...importData, github_token: e.target.value })}
                    placeholder="Auto from Key Vault"
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                  <p className="text-xs mt-1" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                    Override Key Vault PAT if needed
                  </p>
                </div>
              </div>

              {/* Quick Presets */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#94a3b8' }}>
                  Quick Presets
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setImportData({
                      owner: 'MicrosoftDocs',
                      repo: 'cloud-adoption-framework',
                      use_case: 'caf',
                      path: 'docs',
                      branch: 'main',
                    })}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)' }}
                  >
                    CAF Docs
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportData({
                      owner: 'MicrosoftDocs',
                      repo: 'well-architected',
                      use_case: 'waf',
                      path: 'well-architected',
                      branch: 'main',
                    })}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)' }}
                  >
                    WAF Docs
                  </button>
                </div>
              </div>

              {/* Scan Results */}
              {scanResult && (
                <div className="p-4 rounded-lg" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium" style={{ color: '#f1f5f9' }}>
                      Scan Results
                    </h3>
                    <span className="text-sm" style={{ color: '#60a5fa' }}>
                      {scanResult.total_files} files found
                    </span>
                  </div>
                  <div className="max-h-40 overflow-y-auto text-xs" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                    {scanResult.files.slice(0, 50).map((file, i) => (
                      <div key={i} className="py-0.5 truncate">{file}</div>
                    ))}
                    {scanResult.files.length > 50 && (
                      <div className="py-1 text-center" style={{ color: '#94a3b8' }}>
                        ... and {scanResult.files.length - 50} more files
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Import Error */}
              {importError && (
                <div className="p-4 rounded-lg" style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                  <div className="text-sm" style={{ color: '#ef4444' }}>
                    <i className="fa-solid fa-exclamation-triangle mr-2"></i>
                    {importError}
                  </div>
                  <p className="text-xs mt-2" style={{ color: 'rgba(239, 68, 68, 0.7)' }}>
                    Large repositories may timeout. Try using max_files to limit the import, or import in smaller batches.
                  </p>
                </div>
              )}

              {/* Import Results */}
              {importResult && (
                <div className="p-4 rounded-lg" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium" style={{ color: '#f1f5f9' }}>
                      Import Complete
                    </h3>
                    <div className="flex items-center space-x-3 text-sm">
                      {importResult.created > 0 && (
                        <span style={{ color: '#22c55e' }}>
                          <i className="fa-solid fa-plus mr-1"></i>
                          {importResult.created} created
                        </span>
                      )}
                      {importResult.updated > 0 && (
                        <span style={{ color: '#60a5fa' }}>
                          <i className="fa-solid fa-sync mr-1"></i>
                          {importResult.updated} updated
                        </span>
                      )}
                      {importResult.failed > 0 && (
                        <span style={{ color: '#ef4444' }}>
                          <i className="fa-solid fa-times mr-1"></i>
                          {importResult.failed} failed
                        </span>
                      )}
                    </div>
                  </div>
                  {importResult.failed > 0 && (
                    <div className="max-h-32 overflow-y-auto text-xs" style={{ color: 'rgba(239, 68, 68, 0.8)' }}>
                      {importResult.results.filter(r => !r.success).map((r, i) => (
                        <div key={i} className="py-0.5">
                          {r.path}: {r.error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false)
                    resetImport()
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ color: '#94a3b8', background: 'rgba(100, 116, 139, 0.2)' }}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => scanMutation.mutate(importData)}
                  disabled={scanMutation.isPending || !importData.owner || !importData.repo}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50"
                  style={{ background: 'rgba(100, 116, 139, 0.3)', color: '#e2e8f0' }}
                >
                  {scanMutation.isPending ? 'Scanning...' : 'Scan Repository'}
                </button>
                <button
                  type="button"
                  onClick={() => importMutation.mutate(importData)}
                  disabled={importMutation.isPending || !importData.owner || !importData.repo}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
                    color: 'white',
                  }}
                >
                  {importMutation.isPending ? 'Importing...' : 'Import All'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Web Import Modal */}
      {showWebImportModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl p-6"
            style={cardStyle}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold" style={{ color: '#f1f5f9' }}>
                <i className="fa-solid fa-globe mr-2"></i>
                Import from URL
              </h2>
              <button
                onClick={() => {
                  setShowWebImportModal(false)
                  resetWebImport()
                }}
                style={{ color: '#94a3b8' }}
              >
                <i className="fa-solid fa-times text-xl"></i>
              </button>
            </div>

            <div className="space-y-4">
              {/* URL Input */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                  URL
                </label>
                <input
                  type="url"
                  value={webImportData.url}
                  onChange={(e) => setWebImportData({ ...webImportData, url: e.target.value })}
                  placeholder="https://learn.microsoft.com/en-us/azure/..."
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Use Case
                  </label>
                  <select
                    value={webImportData.use_case}
                    onChange={(e) => setWebImportData({ ...webImportData, use_case: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                    style={inputStyle}
                  >
                    {USE_CASE_OPTIONS.slice(1).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#94a3b8' }}>
                    Title Override (optional)
                  </label>
                  <input
                    type="text"
                    value={webImportData.title || ''}
                    onChange={(e) => setWebImportData({ ...webImportData, title: e.target.value })}
                    placeholder="Auto-detected from page"
                    className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={inputStyle}
                  />
                </div>
              </div>

              {/* Preview Results */}
              {webPreviewResult && (
                <div
                  className="p-4 rounded-lg"
                  style={{
                    background: webPreviewResult.success ? 'rgba(30, 41, 59, 0.5)' : 'rgba(239, 68, 68, 0.1)',
                    border: webPreviewResult.success ? '1px solid rgba(100, 180, 255, 0.1)' : '1px solid rgba(239, 68, 68, 0.3)',
                  }}
                >
                  {webPreviewResult.success ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-sm font-medium" style={{ color: '#f1f5f9' }}>
                          {webPreviewResult.title}
                        </h3>
                        <span className="text-sm" style={{ color: '#60a5fa' }}>
                          {webPreviewResult.word_count} words
                        </span>
                      </div>
                      {webPreviewResult.description && (
                        <p className="text-xs mb-2" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                          {webPreviewResult.description}
                        </p>
                      )}
                      <div
                        className="text-xs p-2 rounded max-h-32 overflow-y-auto"
                        style={{ background: 'rgba(0, 0, 0, 0.2)', color: 'rgba(148, 163, 184, 0.7)' }}
                      >
                        {webPreviewResult.content_preview}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm" style={{ color: '#ef4444' }}>
                      <i className="fa-solid fa-exclamation-triangle mr-2"></i>
                      {webPreviewResult.error}
                    </div>
                  )}
                </div>
              )}

              {/* Import Result */}
              {webImportResult && (
                <div
                  className="p-4 rounded-lg"
                  style={{
                    background: webImportResult.success ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    border: webImportResult.success ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                  }}
                >
                  {webImportResult.success ? (
                    <div className="text-sm" style={{ color: '#22c55e' }}>
                      <i className="fa-solid fa-check-circle mr-2"></i>
                      Successfully imported "{webImportResult.title}"
                    </div>
                  ) : (
                    <div className="text-sm" style={{ color: '#ef4444' }}>
                      <i className="fa-solid fa-times-circle mr-2"></i>
                      {webImportResult.error}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowWebImportModal(false)
                    resetWebImport()
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ color: '#94a3b8', background: 'rgba(100, 116, 139, 0.2)' }}
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => webPreviewMutation.mutate(webImportData)}
                  disabled={webPreviewMutation.isPending || !webImportData.url}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50"
                  style={{ background: 'rgba(100, 116, 139, 0.3)', color: '#e2e8f0' }}
                >
                  {webPreviewMutation.isPending ? 'Loading...' : 'Preview'}
                </button>
                <button
                  type="button"
                  onClick={() => webImportMutation.mutate(webImportData)}
                  disabled={webImportMutation.isPending || !webImportData.url}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105 disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
                    color: 'white',
                  }}
                >
                  {webImportMutation.isPending ? 'Importing...' : 'Import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function BestPracticeCard({
  bestPractice,
  onEdit,
  onDelete,
  onRechunk,
}: {
  bestPractice: BestPracticeListItem
  onEdit: () => void
  onDelete: () => void
  onRechunk: () => void
}) {
  const colors = useCaseBadgeColors[bestPractice.use_case] || useCaseBadgeColors.other

  return (
    <div
      className="p-4 rounded-xl transition-all duration-200 hover:scale-[1.02]"
      style={{
        background: 'rgba(15, 23, 42, 0.6)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(100, 180, 255, 0.1)',
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3
            className="text-base font-medium truncate"
            style={{ color: '#f1f5f9' }}
          >
            {bestPractice.title}
          </h3>
        </div>
        <div className="flex items-center space-x-2 ml-2">
          <span
            className="px-2 py-0.5 rounded text-xs font-medium uppercase"
            style={{
              background: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
            }}
          >
            {bestPractice.use_case}
          </span>
          {!bestPractice.is_active && (
            <span
              className="px-2 py-0.5 rounded text-xs"
              style={{
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              Inactive
            </span>
          )}
        </div>
      </div>

      {bestPractice.description && (
        <p
          className="text-sm mb-3 line-clamp-2"
          style={{ color: 'rgba(148, 163, 184, 0.8)' }}
        >
          {bestPractice.description}
        </p>
      )}

      <div className="flex items-center justify-between">
        <div
          className="flex items-center space-x-4 text-xs"
          style={{ color: 'rgba(148, 163, 184, 0.6)' }}
        >
          <span>
            <i className="fa-solid fa-puzzle-piece mr-1"></i>
            {bestPractice.chunk_count} chunks
          </span>
          <span>
            <i className="fa-solid fa-database mr-1"></i>
            {bestPractice.source_type}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onRechunk}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
            style={{ color: '#a78bfa' }}
            title="Re-chunk & Re-embed"
          >
            <i className="fa-solid fa-refresh"></i>
          </button>
          <button
            onClick={onEdit}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
            style={{ color: '#60a5fa' }}
            title="Edit"
          >
            <i className="fa-solid fa-edit"></i>
          </button>
          <button
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-white/10 transition-colors"
            style={{ color: '#ef4444' }}
            title="Delete"
          >
            <i className="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>
    </div>
  )
}
